import React, { useEffect, useState } from "react";

import { useAddPlatformTermsConsent, useAddProgramTermsConsents, useGetFarms, useTranslation, useUserContext } from "utils";

import { ProgramContractDetails, USER_TYPE } from "models";

import { Button, Col, ErrorBoundary, Modal, PageSpin, Row, StickyFooterButtons, Typography } from "components";

import PlatformTerms from "pages/app/_app/components/terms-overlay/PlatformTerms";
import ProgramTerms from "pages/app/_app/components/terms-overlay/ProgramTerms";
import { AccountLogo } from "pages/app/account/components/AccountLogo";

export const TermsOverlay = () => {
  const { t } = useTranslation("dashboard");
  const { user, reloadUserData, setTermsOverlayHidden } = useUserContext();
  const farmsQuery = useGetFarms();

  const [acceptPlatformTerms, setAcceptPlatformTerms] = useState(true);
  const [unsignedProgramContracts, setUnsignedProgramContracts] = useState<ProgramContractDetails[]>([]);
  const [isDecliningTerms, setIsDecliningTerms] = useState<boolean>(false);

  const userType = user?.hasAcceptedPlatformTerms ? USER_TYPE.EXISTING : USER_TYPE.NEW;
  const isNewUser = userType === USER_TYPE.NEW;

  const { mutate: addPlatformTermsConsent } = useAddPlatformTermsConsent({
    accepted: acceptPlatformTerms,
  });
  const { mutate: addProgramTermsConsents } = useAddProgramTermsConsents({
    programConsents: unsignedProgramContracts.map((contract) => ({ programId: contract.programId, accepted: contract.accepted })),
  });

  // Unsigned entries have neither been accepted nor declined
  const { mutate: addUnsignedPlatformTermsConsent } = useAddPlatformTermsConsent({});
  const { mutate: addUnsignedProgramTermsConsents } = useAddProgramTermsConsents({
    programConsents: unsignedProgramContracts.map((contract) => ({ programId: contract.programId })),
  });

  useEffect(() => {
    const details = farmsQuery.data?.reduce((acc, farm) => {
      const programId = farm?.carbonProgram?.id;

      if (programId && !farm.hasAcceptedLatestProgramTerms) {
        const existing = acc.find((item) => item.programId === programId);

        if (existing) {
          existing.farmIds.push(farm.id);
          existing.farmNames.push(farm.name);
        } else {
          acc.push({ programId, farmIds: [farm.id], farmNames: [farm.name], accepted: true });
        }
      }

      return acc;
    }, [] as ProgramContractDetails[]);

    setUnsignedProgramContracts(details ?? []);
  }, [farmsQuery.data]);

  const handleLater = () => {
    if (!user?.hasAcceptedLatestPlatformTerms) {
      addUnsignedPlatformTermsConsent();
      reloadUserData();
    }
    addUnsignedProgramTermsConsents();
    setTermsOverlayHidden(true);
  };

  const handleSubmit = () => {
    if (!user?.hasAcceptedLatestPlatformTerms) {
      addPlatformTermsConsent();
      reloadUserData();
    }
    addProgramTermsConsents();
    setTermsOverlayHidden(true);
  };

  const handleAgree = () => {
    if (!acceptPlatformTerms || unsignedProgramContracts.some((contract) => !contract.accepted)) {
      setIsDecliningTerms(true);
    } else {
      handleSubmit();
    }
  };

  const handleDecline = () => {
    setIsDecliningTerms(true);
  };

  return (
    <div className="terms-overlay margin-top-300 margin-left-300">
      <div className="terms-container">
        <AccountLogo />
        <section className="container terms-content">
          {farmsQuery.isLoading ? (
            <PageSpin />
          ) : (
            <div className="margin-bottom-400">
              <div className="margin-bottom-400">
                <Typography variant="h1" className="margin-bottom-100">
                  {t(`termsOverlay.${userType}.headline`)}
                </Typography>
                <Typography variant="paragraph-m" data-testid="terms-overlay-description">
                  {t(`termsOverlay.${userType}.description`)}
                </Typography>
                {/* Platform Terms */}
                {(isNewUser || !user?.hasAcceptedLatestPlatformTerms) && (
                  <div className="margin-top-400">
                    <Typography variant="h2">{t("termsOverlay.platformTermsHeadline")}</Typography>
                    <PlatformTerms userType={userType} checkboxState={acceptPlatformTerms} setCheckboxState={setAcceptPlatformTerms} />
                  </div>
                )}
                {/* Program Terms */}
                {!!unsignedProgramContracts.length && (
                  <div className="margin-top-400">
                    <Typography variant="h2">{t("termsOverlay.programTermsHeadline")}</Typography>
                    {unsignedProgramContracts.map((record, index) => (
                      <ProgramTerms key={index} contractDetails={record} userType={userType} setCheckboxState={setUnsignedProgramContracts} />
                    ))}
                  </div>
                )}
              </div>
              <StickyFooterButtons>
                {isNewUser ||
                (acceptPlatformTerms && !user?.hasAcceptedLatestPlatformTerms) ||
                unsignedProgramContracts.some((contract) => contract.accepted) ? (
                  <Row align="middle" style={{ width: "100%" }}>
                    {/* prelint-ignore-col */}
                    {!isNewUser && (
                      <Col sm={10}>
                        <Button type="tertiary" onClick={handleLater}>
                          {t("termsOverlay.later")}
                        </Button>
                      </Col>
                    )}
                    <Col sm={isNewUser ? 24 : 14}>
                      <Button className="save-button" dataTestId="terms-agree-button" type="primary" onClick={handleAgree}>
                        {t(`termsOverlay.${isNewUser ? "agree" : "agreeAllChecked"}`)}
                      </Button>
                    </Col>
                  </Row>
                ) : (
                  <Col sm={24} md={24}>
                    <Button className="decline-button" dataTestId="terms-decline-button" type="primary" onClick={handleDecline}>
                      {t("termsOverlay.declineAll")}
                    </Button>
                  </Col>
                )}
              </StickyFooterButtons>
            </div>
          )}
        </section>
      </div>
      <ErrorBoundary>
        <Modal
          showModal={isDecliningTerms}
          onOk={handleSubmit}
          onCancel={() => {
            setIsDecliningTerms(false);
          }}
          okText={t("termsOverlay.decline")}
          cancelText={t("termsOverlay.cancel")}
          title={t("termsOverlay.declineTitle")}
          content={t("termsOverlay.declineContent")}
        />
      </ErrorBoundary>
    </div>
  );
};

export default TermsOverlay;
